import { useCallback } from 'react';
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Text } from 'react-aria-components';
import CompleteProfileForm from 'src/components/auth/CompleteProfileForm';
import { useAuth } from 'src/utils/auth';

import type { ClientToken } from 'src/types/client';

const CompleteProfilePage = () => {
  const {
    email,
    code,
    institutionId,
    programId,
    campusId,
    classId,
    firstName,
    lastName,
  } = Route.useSearch();
  const router = useRouter();
  const auth = useAuth();

  const onCompleted = useCallback(
    async (data: ClientToken) => {
      auth.onLogin(data);
      await router.invalidate();
    },
    [auth, router]
  );

  return (
    <div>
      <CompleteProfileForm
        email={email}
        code={code}
        institutionId={institutionId}
        programId={programId}
        campusId={campusId}
        classId={classId}
        firstName={firstName}
        lastName={lastName}
        onCompleted={onCompleted}
      />
      <Text
        elementType="p"
        className="text-surface-alt-75 font-medium mt-4 text-center"
      >
        Already have an account?{' '}
        <Link
          to="/login"
          className="text-surface-alt-ghost-75 font-semibold hover:underline"
        >
          Sign In
        </Link>
      </Text>
    </div>
  );
};

const CompleteProfileSchema = z.object({
  email: z.string().email(),
  code: z.string().length(64),
  institutionId: z.number().optional(),
  programId: z.number().optional(),
  campusId: z.number().optional(),
  classId: z.number().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export const Route = createFileRoute('/_auth/complete-profile')({
  validateSearch: (search) => CompleteProfileSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = CompleteProfileSchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
        replace: true,
      });
    }
  },
  component: CompleteProfilePage,
});
